// User-Friendly Error Formatting Utility across the HRMS Application

/**
 * Strips technical noise, stack traces, and database exceptions into user-friendly explanations.
 */
export function sanitizeTechnicalText(text) {
  if (!text || typeof text !== 'string') return '';

  const trimmed = text.trim();

  // 1. MongoDB Duplicate Key E11000
  if (trimmed.includes('E11000') || trimmed.includes('duplicate key error')) {
    const dupMatch = trimmed.match(/dup key:\s*(\{.*?\})/);
    if (dupMatch) {
      return `A record with these details already exists (${dupMatch[1]}). Duplicate requests are not permitted.`;
    }
    const indexMatch = trimmed.match(/index:\s*([a-zA-Z0-9_]+)/);
    if (indexMatch) {
      const field = indexMatch[1].replace(/_1/g, '').replace(/_/g, ' ');
      return `A record with this ${field} already exists. Duplicate entries are not permitted.`;
    }
    return 'A record with these details already exists. Duplicate requests are not permitted.';
  }

  // 2. Python Traceback or Unhandled Exceptions
  if (trimmed.includes('Traceback (most recent call last):')) {
    return 'An unexpected system error occurred while processing your request. Please try again or contact HR support.';
  }

  // 3. Mongo / BSON ObjectId casting
  if (trimmed.includes('Cast to ObjectId failed') || trimmed.includes('is not a valid ObjectId')) {
    return 'The requested record identifier is invalid. Please refresh the page and try again.';
  }

  // 4. Python NoneType / AttributeError / KeyError
  if (trimmed.includes("'NoneType' object") || trimmed.includes('AttributeError') || trimmed.includes('KeyError:')) {
    return 'The requested operation could not be completed due to missing data configuration. Please contact your HR administrator.';
  }

  // 5. Network / Axios generic status code strings
  if (/^Request failed with status code 400$/i.test(trimmed)) {
    return 'The request could not be processed. Please verify the entered information and try again.';
  }
  if (/^Request failed with status code 401$/i.test(trimmed)) {
    return 'Your session has expired. Please log in again to continue.';
  }
  if (/^Request failed with status code 403$/i.test(trimmed)) {
    return 'You do not have permission to perform this action. Please contact your manager or HR administrator.';
  }
  if (/^Request failed with status code 404$/i.test(trimmed)) {
    return 'The requested record or resource was not found.';
  }
  if (/^Request failed with status code 409$/i.test(trimmed)) {
    return 'This request conflicts with an existing record or current approval state.';
  }
  if (/^Request failed with status code 422$/i.test(trimmed)) {
    return 'One or more required fields are missing or invalid. Please check your inputs.';
  }
  if (/^Request failed with status code 5\d\d$/i.test(trimmed) || /^Internal Server Error$/i.test(trimmed)) {
    return 'A server error occurred while processing your request. Please try again in a few moments or contact support.';
  }
  if (/^Network Error$/i.test(trimmed)) {
    return 'Unable to reach the server. Please check your network connection and try again.';
  }

  return trimmed;
}

/**
 * Format structured detail objects (Pydantic lists, date block objects, policy objects).
 */
export function formatDetail(detail) {
  if (detail == null) return '';

  if (typeof detail === 'string') {
    return sanitizeTechnicalText(detail);
  }

  // Array of errors (e.g. FastAPI / Pydantic validation)
  if (Array.isArray(detail)) {
    const formatted = detail
      .map((e) => {
        if (!e) return '';
        if (typeof e === 'string') return sanitizeTechnicalText(e);
        if (typeof e === 'object') {
          const loc = Array.isArray(e.loc)
            ? e.loc.filter((x) => x !== 'body' && x !== 'query' && x !== 'path').join('.')
            : '';
          const rawMsg = typeof e.msg === 'string' ? e.msg : (typeof e.message === 'string' ? e.message : JSON.stringify(e));
          const cleanMsg = sanitizeTechnicalText(rawMsg);
          if (loc) {
            const field = loc.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return `${field}: ${cleanMsg}`;
          }
          return cleanMsg;
        }
        return String(e);
      })
      .filter(Boolean)
      .join('; ');

    return formatted || 'Validation error: please check all required fields.';
  }

  // Object shape (e.g., policy validation result, date block info, or { message, reason })
  if (typeof detail === 'object') {
    // 1. Direct message or msg field
    const primaryMsg = detail.message || detail.msg || detail.detail || detail.reason || detail.policy_violation_message || detail.error;
    if (typeof primaryMsg === 'string' && primaryMsg.trim()) {
      let result = sanitizeTechnicalText(primaryMsg);
      if (detail.earliest_allowed_date && !result.includes(detail.earliest_allowed_date)) {
        result += ` (Earliest allowed date: ${detail.earliest_allowed_date})`;
      }
      if (detail.policy_name && !result.toLowerCase().includes(detail.policy_name.toLowerCase())) {
        result += ` [Policy: ${detail.policy_name}]`;
      }
      return result;
    }

    // 2. Date Block / Window specifics
    if (detail.is_valid === false) {
      const mod = (detail.module || 'Request').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const reqDate = detail.request_date || '';
      const earliest = detail.earliest_allowed_date || '';
      const polName = detail.policy_name || 'attendance policy';
      if (reqDate && earliest) {
        return `You cannot apply for ${mod} on ${reqDate} because it is outside the permitted policy window (earliest allowed date: ${earliest}). Policy: ${polName}.`;
      }
      if (detail.message) {
        return sanitizeTechnicalText(detail.message);
      }
    }

    // 3. Pydantic errors list inside detail.errors
    if (Array.isArray(detail.errors)) {
      return formatDetail(detail.errors);
    }

    // 4. Fallback: extract string values from object
    const values = Object.values(detail).filter((v) => typeof v === 'string' && v.trim());
    if (values.length > 0) {
      return sanitizeTechnicalText(values.join('; '));
    }
  }

  return 'An unexpected issue occurred. Please check your request.';
}

/**
 * Format any API error (Axios error, string, error object) into a clear, user-facing string.
 */
export function formatApiError(err, fallback = 'An unexpected error occurred. Please try again.') {
  if (!err) return fallback;

  // If already a plain string
  if (typeof err === 'string') {
    const sanitized = sanitizeTechnicalText(err);
    return sanitized || fallback;
  }

  const res = err.response;
  const status = res?.status;
  const data = res?.data;

  // 1. Check data fields in priority order
  const candidate =
    data?.detail ??
    data?.message ??
    data?.error ??
    data?.policy_violation_message ??
    data?.blocking_error ??
    data?.error_description ??
    err.detail ??
    err.userFacingMessage;

  if (candidate != null) {
    const formatted = formatDetail(candidate);
    if (formatted && !/^Request failed with status code/i.test(formatted)) {
      return formatted;
    }
  }

  // 2. HTTP Status Code fallbacks
  if (status === 400) {
    return 'The request was invalid or could not be processed. Please verify your input details.';
  }
  if (status === 401) {
    return 'Your session has expired. Please log in again to continue.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this action. Please contact your manager or HR administrator.';
  }
  if (status === 404) {
    return 'The requested record or resource was not found.';
  }
  if (status === 409) {
    return 'This request conflicts with an existing record or current approval state.';
  }
  if (status === 422) {
    return 'Validation failed. Please ensure all mandatory fields are provided with valid values.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'The server is temporarily unavailable. Please verify your connection and try again in a moment.';
  }
  if (status >= 500) {
    return 'An internal server error occurred while processing your request. Please try again or contact HR support.';
  }

  // 3. Network or Axios client errors
  if (err.message) {
    const sanitizedMsg = sanitizeTechnicalText(err.message);
    if (sanitizedMsg && !/^Request failed with status code/i.test(sanitizedMsg)) {
      return sanitizedMsg;
    }
  }

  return fallback;
}

/**
 * Returns structured error details for advanced UI presentation (alerts, cards).
 */
export function parseApiError(err) {
  const fullMsg = formatApiError(err);
  const status = err?.response?.status;
  const data = err?.response?.data;

  let title = 'Action Failed';
  if (status === 403) title = 'Permission Restriction';
  else if (status === 409) title = 'Duplicate / Conflict';
  else if (status === 404) title = 'Not Found';
  else if (status === 422) title = 'Validation Error';
  else if (status >= 500) title = 'System Error';

  return {
    title,
    message: fullMsg,
    status,
    policy: data?.policy_name || data?.detail?.policy_name || null,
    earliestDate: data?.earliest_allowed_date || data?.detail?.earliest_allowed_date || null,
  };
}
